import React, { useState } from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ModalBox from 'components/application/ModalBox';
import RadioField from 'components/application/RadioField';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import GeneratingExportModal from 'components/exports/components/GeneratingExportModal';

export default function ExportProjectsModal({
  isExportProjectsDisabled,
  isFilteredSelectionDisabled,
  isGenerating,
  onClose,
  onSubmit,
}) {
  const {
    division: {
      attributes: { pluralRAMSName },
    },
  } = useCurrentActor();

  const [projectSelection, setProjectSelection] = useState(
    isFilteredSelectionDisabled ? 'unfiltered' : 'filtered',
  );

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'projectsDashboard.components.ExportProjectsModal',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return isGenerating ?
      <GeneratingExportModal onClose={onClose} />
    : <ModalBox
        customFooter={
          <div className='modalbox__footer-actions modalbox__footer-actions--right'>
            <TextButton
              className='tw-mr-3'
              color='blue'
              onClick={onClose}
              size='md'
            >
              {i18nTShared('cancel')}
            </TextButton>
            <FilledButton
              color='mint'
              disabled={isExportProjectsDisabled}
              onClick={() => onSubmit({ projectSelection })}
            >
              {i18nT('button')}
            </FilledButton>
          </div>
        }
        isOpen={true}
        mode='flexible'
        onClose={onClose}
      >
        <div className='tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-p-6'>
          <p className='tw-mb-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
            {i18nT('title', {
              interpolation: { escapeValue: false },
              pluralRAMSName,
            })}
          </p>
        </div>
        <div className='tw-flex tw-flex-col tw-gap-4 tw-p-6'>
          <p className='tw-mb-0 tw-text-l tw-font-semibold tw-tracking-auto'>
            {i18nT('description')}
          </p>
          <div className='tw-flex tw-flex-col tw-gap-3'>
            <RadioField
              checked={projectSelection === 'filtered'}
              className='tw-m-0'
              disabled={isFilteredSelectionDisabled}
              key='filtered'
              label={
                <p className='tw-mb-0 tw-text-m tw-font-normal tw-tracking-auto'>
                  {i18nT('labels.filtered', {
                    interpolation: { escapeValue: false },
                    pluralRAMSName,
                  })}
                </p>
              }
              name='projectSelection'
              onChange={() => setProjectSelection('filtered')}
              value='filtered'
            />
            <RadioField
              checked={projectSelection === 'unfiltered'}
              className='tw-m-0'
              key='unfiltered'
              label={
                <p className='tw-mb-0 tw-text-m tw-font-normal tw-tracking-auto'>
                  {i18nT('labels.unfiltered', {
                    interpolation: { escapeValue: false },
                    pluralRAMSName,
                  })}
                </p>
              }
              name='projectSelection'
              onChange={() => setProjectSelection('unfiltered')}
              value='unfiltered'
            />
          </div>
        </div>
      </ModalBox>;
}

ExportProjectsModal.propTypes = {
  isExportProjectsDisabled: PropTypes.bool,
  isFilteredSelectionDisabled: PropTypes.bool.isRequired,
  isGenerating: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
